import React from "react";
import { Edit } from "lucide-react";
import type { RevenueBreakdownPanelProps } from "./RevenueBreakdownPanel.types";

export const RevenueBreakdownPanel: React.FC<RevenueBreakdownPanelProps> = ({
  revenuesByCategory,
  currentSnapshot,
  calculateActualRevenueAmount,
  onRevenueClick,
}) => {
  return (
    <>
      <h4 className="mb-4 font-medium text-revenue-muted">
        Monthly Revenue ($
        {currentSnapshot?.monthlyRevenue.toFixed(2) || "0.00"})
      </h4>

      {Object.keys(revenuesByCategory).length === 0 ? (
        <div className="mb-6 text-sm text-white/45">
          No revenue streams at this time
        </div>
      ) : (
        <div className="mb-8 space-y-3">
          {Object.keys(revenuesByCategory).map((category) => {
            const categoryRevenues = revenuesByCategory[category];
            const categoryTotal = categoryRevenues.reduce(
              (sum, rev) => sum + calculateActualRevenueAmount(rev),
              0,
            );
            const maxRevenue = Math.max(
              ...Object.keys(revenuesByCategory).map((cat) =>
                revenuesByCategory[cat].reduce(
                  (sum, rev) => sum + calculateActualRevenueAmount(rev),
                  0,
                ),
              ),
              1,
            );
            const categoryWidth = (categoryTotal / maxRevenue) * 100;

            return (
              <div key={`revenue-${category}`} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-revenue-muted">
                    {category}
                  </span>
                  <span className="text-sm text-brand">
                    +${categoryTotal.toFixed(2)}
                  </span>
                </div>

                <div className="h-2 w-full rounded-full bg-revenue/15">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-revenue-muted to-brand transition-all duration-300"
                    style={{ width: `${categoryWidth}%` }}
                  />
                </div>

                <div className="ml-2 space-y-1">
                  {categoryRevenues.map((revenue) => {
                    const isCustomerProjection = Boolean(
                      (revenue as { isCustomerProjection?: boolean })
                        .isCustomerProjection,
                    );
                    const editHint = isCustomerProjection
                      ? "Click to edit customer projection"
                      : `Click to edit: ${revenue.name}`;

                    return (
                    <div
                      key={revenue.id}
                      className="group flex cursor-pointer items-center justify-between rounded p-2 text-xs text-brand transition-all duration-200 hover:scale-[1.02] hover:bg-brand/10 hover:text-brand-muted"
                      onClick={() => onRevenueClick?.(revenue.id)}
                      title={editHint}
                    >
                      <span className="transition-all duration-200 group-hover:font-medium">
                        {revenue.name}
                      </span>
                      <div className="flex items-center gap-1">
                        <span>
                          +$
                          {calculateActualRevenueAmount(
                            revenue,
                          ).toFixed(2)}
                        </span>
                        <span className="text-white/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          <Edit className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
